import { Paper } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Report, ReportMailScheduleTypes, ReportTemplate, ReportTemplateTypes } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import TitleBox from "../../components/TitleBox";
import { StyledLink } from "../../components/utility/StyledLink";
import type { AppColumn, AppScalarValue } from "../../components/tables/AppTable.types";
import ReportsContext from "../../contexts/ReportsContext";
import useReports from "../../hooks/data/useReports";
import useReportTemplates from "../../hooks/data/useReportTemplates";
import { getScheduleString } from "../../utility/EnumValueGetters";
import EmailOnlyReports from "./EmailOnlyReports";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";

export const buildReportColumns = (templates: readonly ReportTemplate[]): AppColumn<Report>[] => {
    const templateOptions = templates.flatMap((template) =>
        template.id === null || template.id === undefined
            ? []
            : [
                  {
                      value: template.id,
                      label: template.name ?? String(template.id),
                  },
              ],
    );
    const templateLabel = (value: AppScalarValue) =>
        templates.find((template) => template.id === value)?.name ?? String(value ?? "");

    return [
        {
            id: "name",
            label: "Name",
            accessor: (row) => row.name,
            scalarType: "text",
            flex: 1,
            cell: ({ row, formatted }) => <StyledLink to={`/reports/preview/${row.id}`}>{formatted}</StyledLink>,
        },
        {
            id: "templateId",
            label: "Template",
            accessor: (row) => row.templateId,
            scalarType: "lookup",
            flex: 1,
            lookup: {
                options: templateOptions,
                labelFor: (value) => templateLabel(value),
            },
            sortAccessor: (row) => templateLabel(row.templateId),
            sortScalarType: "text",
        },
        {
            id: "scheduleType",
            label: "Schedule",
            accessor: (row) => row.scheduleType,
            scalarType: "lookup",
            width: 200,
            lookup: {
                options: [
                    {
                        value: ReportMailScheduleTypes.Never,
                        label: "Never",
                    },
                    {
                        value: ReportMailScheduleTypes.Daily,
                        label: "Daily",
                    },
                    {
                        value: ReportMailScheduleTypes.Weekly,
                        label: "Weekly",
                    },
                    {
                        value: ReportMailScheduleTypes.Monthly,
                        label: "Monthly",
                    },
                    {
                        value: ReportMailScheduleTypes.Yearly,
                        label: "Yearly",
                    },
                ],
                labelFor: (value) => getScheduleString(scheduleType(value)),
            },
            formatter: (value) => getScheduleString(scheduleType(value)),
            exportAccessor: ({ value }) => getScheduleString(scheduleType(value)),
            sortAccessor: (row) => getScheduleString(row.scheduleType),
            sortScalarType: "text",
        },
        {
            id: "actions",
            label: "",
            headerLabel: "",
            accessor: () => null,
            scalarType: "text",
            kind: "action",
            width: 100,
            align: "right",
            filterable: false,
            sortable: false,
            groupable: false,
            exportable: false,
            cell: ({ row }) => (
                <GridActions editUrl={`/reports/edit/${row.id}`} deleteUrl={`/reports/delete/${row.id}`} />
            ),
        },
    ];
};

function scheduleType(value: AppScalarValue): ReportMailScheduleTypes | undefined {
    return typeof value === "number" ? (value as ReportMailScheduleTypes) : undefined;
}

function createReportRowIds(reports: readonly Report[]): (row: Report) => string | number {
    const fallbackIds = new WeakMap<Report, string>();

    reports.forEach((report, index) => fallbackIds.set(report, `report-row-${index}`));

    return (row) => row.id ?? fallbackIds.get(row) ?? "report-row-unmapped";
}

const Reports: FC = () => {
    const emailOnlyTemplateTypes = [ReportTemplateTypes.EmailOnlySalesSummary, ReportTemplateTypes.EmailOnlyLotSummary];
    const contextValue = useReports();
    const { data: reports } = contextValue;
    const templatesContextValue = useReportTemplates();
    const { data: reportTemplates } = templatesContextValue;

    const emailOnlyReports = useMemo(
        () =>
            reports?.filter((report) => {
                const templateType = reportTemplates?.find(
                    (template) => template.id == report.templateId,
                )?.templateType;

                return templateType && emailOnlyTemplateTypes.includes(templateType);
            }),
        [reports, reportTemplates],
    );

    const nonEmailOnlyReports = useMemo(
        () =>
            reports?.filter((report) => {
                const templateType = reportTemplates?.find(
                    (template) => template.id == report.templateId,
                )?.templateType;

                return !templateType || !emailOnlyTemplateTypes.includes(templateType);
            }),
        [reports, reportTemplates],
    );

    const sourceReports = reports ?? [];
    const getRowId = useMemo(() => createReportRowIds(sourceReports), [reports]);
    const reportColumns = useMemo(() => buildReportColumns(reportTemplates ?? []), [reportTemplates]);

    const exportMetadata = useMemo(
        () => ({
            fileName: `Reports`,
            exportTitle: `Reports`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <ReportTemplatesContext.Provider value={templatesContextValue}>
            <ReportsContext.Provider value={contextValue}>
                <SetLinks stageLinkEnabled={false} lotLinkEnabled={false} />
                <Outlet />
                <TitleBox
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                    }}
                >
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={"/reports/templates"}
                    >
                        Templates
                    </StyledButton>
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={"/reports/create"}
                    >
                        New Report
                    </StyledButton>
                </TitleBox>
                <EmailOnlyReports reports={emailOnlyReports ?? []} getRowId={getRowId} />
                <Paper>
                    <CollapsibleAppTable
                        density="compact"
                        columns={reportColumns}
                        title={"Reports"}
                        loading={false}
                        rows={nonEmailOnlyReports ?? []}
                        getRowId={getRowId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Paper>
            </ReportsContext.Provider>
        </ReportTemplatesContext.Provider>
    );
};

export default Reports;
